import { useState } from 'react';
import { format, parse, isBefore, startOfDay } from 'date-fns';
import { sv } from 'date-fns/locale';
import { X, Calendar as CalendarIcon, AlertTriangle } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

interface OccurrenceDatePickerProps {
  selectedDates: string[]; // ISO date strings (YYYY-MM-DD)
  onDatesChange: (dates: string[]) => void;
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  disabled?: boolean;
}

export function OccurrenceDatePicker({
  selectedDates,
  onDatesChange,
  startTime,
  endTime,
  disabled = false,
}: OccurrenceDatePickerProps) {
  const [month, setMonth] = useState<Date>(new Date());
  
  const today = startOfDay(new Date());
  
  // Convert string dates to Date objects for the calendar
  const selectedDatesAsDate = selectedDates
    .map(d => parse(d, 'yyyy-MM-dd', new Date()))
    .filter(d => !isNaN(d.getTime()));

  // Handle date selection/deselection
  const handleSelect = (selectedDay: Date, activeModifiers: Record<string, boolean>) => {
    if (!selectedDay || disabled) return;
    
    const dateStr = format(selectedDay, 'yyyy-MM-dd');
    
    if (activeModifiers.selected) {
      // Deselect
      onDatesChange(selectedDates.filter(d => d !== dateStr));
    } else {
      // Select
      onDatesChange([...selectedDates, dateStr].sort());
    }
  };

  // Remove a specific date
  const handleRemoveDate = (dateStr: string) => {
    if (disabled) return;
    onDatesChange(selectedDates.filter(d => d !== dateStr));
  };

  // Format a date for display
  const formatDisplayDate = (dateStr: string) => {
    const date = parse(dateStr, 'yyyy-MM-dd', new Date());
    return format(date, "EEE d MMM yyyy", { locale: sv });
  };

  // Check if a date is in the past
  const isDateInPast = (dateStr: string) => {
    const date = parse(dateStr, 'yyyy-MM-dd', new Date());
    return isBefore(startOfDay(date), today);
  };

  const pastDatesCount = selectedDates.filter(isDateInPast).length;

  return (
    <div className="space-y-4">
      {/* Calendar */}
      <div className="flex justify-center">
        <Calendar
          mode="multiple"
          selected={selectedDatesAsDate}
          onDayClick={handleSelect}
          month={month}
          onMonthChange={setMonth}
          disabled={(date) => isBefore(startOfDay(date), today)}
          className={cn("rounded-md border pointer-events-auto", disabled && "opacity-50 pointer-events-none")}
          classNames={{
            day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
          }}
        />
      </div>

      {/* Time info */}
      <div className="text-sm text-muted-foreground text-center">
        Alla tillfällen körs kl {startTime} - {endTime}
      </div>

      {/* Warning if no dates selected */}
      {selectedDates.length === 0 && (
        <Alert variant="destructive" className="bg-destructive/10 border-destructive/20">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Välj minst ett datum för att skapa tillfällen
          </AlertDescription>
        </Alert>
      )}

      {/* Warning about past dates */}
      {pastDatesCount > 0 && (
        <Alert className="bg-amber-500/10 border-amber-500/20">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <AlertDescription className="text-amber-700">
            {pastDatesCount} datum har passerat och kommer ignoreras
          </AlertDescription>
        </Alert>
      )}

      {/* Selected dates list */}
      {selectedDates.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              Valda datum ({selectedDates.length})
            </span>
            {selectedDates.length > 1 && !disabled && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onDatesChange([])}
                className="text-muted-foreground hover:text-destructive"
              >
                Rensa alla
              </Button>
            )}
          </div>
          
          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 bg-muted/30 rounded-lg">
            {selectedDates.map((dateStr) => {
              const isPast = isDateInPast(dateStr);
              return (
                <Badge
                  key={dateStr}
                  variant={isPast ? "secondary" : "default"}
                  className={cn(
                    "flex items-center gap-1.5 pr-1",
                    isPast && "opacity-50 line-through"
                  )}
                >
                  <CalendarIcon className="h-3 w-3" />
                  {formatDisplayDate(dateStr)}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => handleRemoveDate(dateStr)}
                      className="ml-1 rounded-full hover:bg-primary-foreground/20 p-0.5"
                      aria-label={`Ta bort ${formatDisplayDate(dateStr)}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
